import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as sipService from "../services/sipService";
import {
  NativeMediaSession,
  NATIVE_MEDIA_STATUS,
  isUuid,
} from "../services/nativeMediaService";
import sipApi from "../services/sipApi";
import { toast } from "react-hot-toast";
import { useAuth } from "./AuthContext";
import { useSocketContext } from "./SocketContext";

import { toCamel } from "../utils/caseConverter";
const SipContext = createContext(null);

const EMPTY_NATIVE_MEDIA = Object.freeze({
  status: NATIVE_MEDIA_STATUS.IDLE,
  callId: null,
  ownerId: null,
  negotiationId: null,
  generation: null,
  direction: null,
  browserRole: null,
  expected: null,
  deadlineAt: null,
  error: null,
  capabilities: null,
  recording: false,
  recordingStatus: "stopped",
  recordingPending: false,
  recordingUrl: null,
  transcription: null,
  recordingError: null,
});

const TERMINAL_NATIVE_CALL_STATUSES = new Set([
  "completed",
  "ended",
  "failed",
  "no_answer",
  "busy",
  "abandoned",
  "cancelled",
  "rejected",
]);

const nativeCallIdentity = (value) => {
  const record = toCamel(value?.data || value);
  const callId = record?.id || record?.callId;
  const status = String(record?.status || "").toLowerCase();
  if (
    !record ||
    record.backend !== "native_sip" ||
    !isUuid(callId) ||
    !["inbound", "outbound"].includes(record.direction) ||
    TERMINAL_NATIVE_CALL_STATUSES.has(status)
  ) {
    return null;
  }

  return {
    ...record,
    id: callId.toLowerCase(),
    callId: callId.toLowerCase(),
    direction: record.direction,
    phase: record.phase,
    backend: "native_sip",
  };
};

const initialNativeCallRecord = (value) => {
  const record = nativeCallIdentity(value);
  return record?.phase === "awaiting_media" ? record : null;
};

const rejoinableNativeCallRecord = (value) => nativeCallIdentity(value);

export const SipProvider = ({ children }) => {
  const { user } = useAuth();
  const {
    socket,
    isConnected: isSocketConnected,
    subscribe,
  } = useSocketContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  const [callInfo, setCallInfo] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const nativeCallIdRef = useRef(null);
  const [connectionError, setConnectionError] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [nativeCallInfo, setNativeCallInfo] = useState(null);
  const [nativeMedia, setNativeMedia] = useState(EMPTY_NATIVE_MEDIA);
  const audioRef = useRef(null);
  const nativeSessionRef = useRef(null);
  const nativeSessionEpochRef = useRef(0);

  const ensureAudioElement = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.autoplay = true;
      audio.playsInline = true;
      audioRef.current = audio;
    }
    return audioRef.current;
  }, []);

  const setAudioElement = useCallback((element) => {
    if (!element || element === audioRef.current) return;
    audioRef.current = element;
    sipService.updateAudioElement(element);
    if (nativeSessionRef.current) {
      nativeSessionRef.current.setAudioElement(element);
    }
  }, []);

  const closeNativeMedia = useCallback((status = NATIVE_MEDIA_STATUS.IDLE) => {
    nativeSessionEpochRef.current += 1;
    const session = nativeSessionRef.current;
    nativeSessionRef.current = null;
    session?.close(status);
    nativeCallIdRef.current = null;
    if (status === NATIVE_MEDIA_STATUS.IDLE) {
      setNativeCallInfo(null);
      setNativeMedia(EMPTY_NATIVE_MEDIA);
    }
  }, []);

  const prepareNativeCall = useCallback(
    async (value, { connectAudio = false, rejoin = false } = {}) => {
      const record = rejoin
        ? rejoinableNativeCallRecord(value)
        : initialNativeCallRecord(value);
      const ownerId = user?.id;
      if (!record || !isUuid(ownerId)) {
        throw new Error(
          "The server did not return an owned native media call.",
        );
      }
      if (
        record.ownerId &&
        String(record.ownerId).toLowerCase() !== String(ownerId).toLowerCase()
      ) {
        throw new Error("The native call belongs to a different owner.");
      }
      if (!socket || !isSocketConnected) {
        closeNativeMedia();
        setNativeCallInfo(record);
        setNativeMedia({
          ...EMPTY_NATIVE_MEDIA,
          status: NATIVE_MEDIA_STATUS.WAITING,
          callId: record.id,
          ownerId: String(ownerId).toLowerCase(),
          direction: record.direction,
          error: "Real-time media signaling is reconnecting.",
        });
        throw new Error("Real-time media signaling is unavailable.");
      }

      const existing = nativeSessionRef.current;
      if (existing?.callId === record.id && !existing.terminated) {
        if (connectAudio) await existing.connectAudio();
        return existing.state;
      }

      closeNativeMedia();
      const epoch = ++nativeSessionEpochRef.current;
      const session = new NativeMediaSession({
        socket,
        callId: record.id,
        ownerId,
        audioElement: ensureAudioElement(),
        onState: (next) => {
          if (
            nativeSessionEpochRef.current !== epoch ||
            nativeSessionRef.current !== session
          ) {
            return;
          }
          setNativeMedia(next);
          if (typeof next.predictiveAgentConnected === "boolean" ||
              next.status === NATIVE_MEDIA_STATUS.ENDED) {
            setNativeCallInfo((current) => current?.id === record.id ? {
              ...current,
              ...(typeof next.predictiveAgentConnected === "boolean"
                ? { predictiveAgentConnected: next.predictiveAgentConnected } : {}),
              ...(next.status === NATIVE_MEDIA_STATUS.ENDED ? { phase: "ended" } : {}),
            } : current);
          }
        },
      });

      nativeSessionRef.current = session;
      nativeCallIdRef.current = record.id;
      setNativeCallInfo(record);
      setNativeMedia({
        ...EMPTY_NATIVE_MEDIA,
        status: NATIVE_MEDIA_STATUS.WAITING,
        callId: record.id,
        ownerId: String(ownerId).toLowerCase(),
        direction: record.direction,
      });

      try {
        await session.join();
        if (connectAudio) await session.connectAudio();
        return session.state;
      } catch (error) {
        if (nativeSessionRef.current === session) {
          setNativeMedia((current) => ({
            ...current,
            status: NATIVE_MEDIA_STATUS.FAILED,
            error: error.message || "Native media could not be prepared.",
          }));
        }
        throw error;
      }
    },
    [closeNativeMedia, ensureAudioElement, isSocketConnected, socket, user?.id],
  );

  useEffect(() => {
    if (
      !socket ||
      !isSocketConnected ||
      !nativeCallInfo ||
      nativeCallInfo.phase === "ended" ||
      (nativeSessionRef.current && !nativeSessionRef.current.terminated)
    ) {
      return;
    }

    // A canonical call may be created while the authenticated Phoenix socket
    // is reconnecting. Preserve that call and join its channel when signaling
    // returns; microphone capture still requires the explicit Connect Audio
    // gesture.
    void prepareNativeCall(nativeCallInfo, { rejoin: true }).catch((error) => {
      setNativeMedia((current) => ({
        ...current,
        status: NATIVE_MEDIA_STATUS.FAILED,
        error: error.message || "Native media signaling could not reconnect.",
      }));
    });
  }, [isSocketConnected, nativeCallInfo, prepareNativeCall, socket]);

  const connectNativeAudio = useCallback(async () => {
    const session = nativeSessionRef.current;
    if (!session || session.terminated) {
      if (!nativeCallInfo) {
        toast.error("There is no native call waiting for audio.");
        return false;
      }
      try {
        await prepareNativeCall(nativeCallInfo, {
          connectAudio: true,
          rejoin: true,
        });
        return true;
      } catch (error) {
        toast.error(error.message || "Could not connect call audio.");
        return false;
      }
    }

    try {
      await session.connectAudio();
      return true;
    } catch (error) {
      toast.error(error.message || "Could not connect call audio.");
      return false;
    }
  }, [nativeCallInfo, prepareNativeCall]);

  const nativeControl = useCallback(async (action, payload = {}) => {
    const session = nativeSessionRef.current;
    if (!session) {
      toast.error("The native call control channel is unavailable.");
      return false;
    }

    try {
      await session.control(action, payload);
      return true;
    } catch (error) {
      toast.error(error.message || `Failed to ${action} the native call.`);
      return false;
    }
  }, []);

  const nativeRecording = useCallback(async (command) => {
    const session = nativeSessionRef.current;
    if (!session || session.terminated) {
      throw new Error("The native call channel is unavailable.");
    }
    if (
      !["startRecording", "stopRecording", "recordingStatus"].includes(command)
    ) {
      throw new Error("Unsupported native recording operation.");
    }
    return session[command]();
  }, []);

  const hangupNativeCall = useCallback(async () => {
    let session = nativeSessionRef.current;
    if ((!session || session.terminated) && nativeCallInfo) {
      try {
        await prepareNativeCall(nativeCallInfo, { rejoin: true });
        session = nativeSessionRef.current;
      } catch (_) {
        session = nativeSessionRef.current;
      }
    }
    if (!session || session.terminated) {
      toast.error("The native call control channel is unavailable.");
      return false;
    }
    try {
      const reply = toCamel(await session.hangup());
      if (nativeSessionRef.current === session) {
        nativeSessionRef.current = null;
      }
      setNativeCallInfo(null);
      return { ...nativeCallInfo, ...reply, id: session.callId };
    } catch (error) {
      toast.error(error.message || "Failed to end the native call.");
      return false;
    }
  }, [nativeCallInfo, prepareNativeCall]);

  const handleCallState = useCallback(
    (payload) => {
      const { event, callId: wireCallId, phase, status, cause, digit, extension,
        dialingMode, predictiveAgentConnected } = toCamel(payload);
      const callId = sipService.parseNexusCallId(wireCallId);
      const nativeSession = nativeSessionRef.current;
      const isCurrentNative = Boolean(
        callId &&
        (nativeSession?.callId === callId ||
          nativeCallIdRef.current === callId),
      );

      if (isCurrentNative) {
        setNativeCallInfo((previous) =>
          previous
            ? {
                ...previous,
                phase: phase || event,
                status,
                ...(dialingMode ? { dialingMode } : {}),
                ...(typeof predictiveAgentConnected === "boolean"
                  ? { predictiveAgentConnected } : {}),
              }
            : previous,
        );
        if (
          [event, phase, status].some((value) =>
            ["ended", "completed", "failed", "transferred"].includes(value),
          )
        ) {
          nativeSession?.endFromServer();
          nativeSessionRef.current = null;
        }
        return;
      }

      const updateTrackedSipCall = (nextPhase) => {
        if (!callId || isCurrentNative) return;
        setCallInfo((previous) => ({
          ...(previous || {}),
          id: callId,
          callId,
          phase: nextPhase,
        }));
      };

      switch (event) {
        case "ringing":
        case "agent_ringing":
        case "agent_answered":
        case "customer_dialing": {
          if (!isCurrentNative) setCallStatus(phase || event);
          updateTrackedSipCall(phase || event);
          break;
        }
        case "answered": {
          const nextPhase = phase || "customer_answered";
          if (!isCurrentNative) setCallStatus(nextPhase);
          updateTrackedSipCall(nextPhase);
          sipService.notifyFsmListeners("accepted", {
            id: callId,
            callId,
            status,
            phase: nextPhase,
          });
          break;
        }
        case "on_hold":
          if (!isCurrentNative) setCallStatus("on_hold");
          updateTrackedSipCall("on_hold");
          sipService.notifyFsmListeners("on_hold", { id: callId, callId });
          break;
        case "active":
          if (!isCurrentNative) setCallStatus("active");
          updateTrackedSipCall("active");
          sipService.notifyFsmListeners("active", { id: callId, callId });
          break;
        case "dtmf":
          sipService.notifyFsmListeners("dtmf", digit);
          break;
        case "transferred":
          sipService.notifyFsmListeners(
            "transferred",
            extension || { id: callId, callId },
          );
          if (!isCurrentNative) {
            setCallStatus("ended");
            setCallInfo(null);
            setIsMuted(false);
          }
          break;
        case "muted":
          if (!isCurrentNative) setIsMuted(true);
          sipService.notifyFsmListeners("muted", true);
          break;
        case "unmuted":
          if (!isCurrentNative) setIsMuted(false);
          sipService.notifyFsmListeners("muted", false);
          break;
        case "ended":
          if (!isCurrentNative) {
            setCallStatus("ended");
            setCallInfo(null);
            setIsMuted(false);
          }
          sipService.notifyFsmListeners("ended", {
            id: callId,
            callId,
            status,
            cause,
          });
          break;
        default:
          break;
      }
    },
    [],
  );

  // The legacy organization call channel carries Asterisk/FSM lifecycle only.
  // Native SDP is always isolated to the owner-authorized call UUID channel.
  useEffect(() => {
    if (!socket || !isSocketConnected || !user?.organizationId)
      return undefined;

    const channel = socket.channel(`call:${user.organizationId}`, {});
    const stateRef = channel.on("call:state", handleCallState);
    channel
      .join()
      .receive("error", () =>
        setConnectionError("Could not subscribe to call lifecycle updates."),
      );

    return () => {
      channel.off("call:state", stateRef);
      channel.leave();
    };
  }, [handleCallState, isSocketConnected, socket, user?.organizationId]);

  // Native inbound notifications carry identifiers only. Joining retrieves the
  // authoritative negotiation state; microphone access waits for Answer.
  useEffect(() => {
    if (!isSocketConnected || !user?.id) return undefined;

    return subscribe("call:incoming", (payload) => {
      const record = initialNativeCallRecord(payload);
      if (!record || record.direction !== "inbound") return;
      if (
        !isUuid(record.ownerId) ||
        record.ownerId.toLowerCase() !== String(user.id).toLowerCase()
      ) {
        return;
      }

      prepareNativeCall(record).catch((error) => {
        toast.error(error.message || "Could not prepare the incoming call.");
      });
    });
  }, [isSocketConnected, prepareNativeCall, subscribe, user?.id]);

  // Predictive ownership starts only after customer answer. No microphone or
  // unrelated SDP is used until the assigned agent explicitly connects audio.
  useEffect(() => {
    if (!isSocketConnected || !user?.id) return undefined;
    return subscribe("call:assigned", (payload) => {
      const record = rejoinableNativeCallRecord(payload);
      if (!record || record.direction !== "outbound" ||
          record.dialingMode !== "predictive" ||
          !isUuid(record.ownerId) ||
          record.ownerId.toLowerCase() !== String(user.id).toLowerCase()) return;
      void prepareNativeCall(record, { rejoin: true }).catch((error) => {
        toast.error(error.message || "Could not attach the assigned customer call.");
      });
    });
  }, [isSocketConnected, prepareNativeCall, subscribe, user?.id]);

  // Registered SIP.js endpoint initialization remains separate from native
  // call-channel media. Registration itself never requests microphone access.
  useEffect(() => {
    let active = true;
    sipService.disconnect();
    setIsInitialized(false);
    setIsConnected(false);
    setIsRegistered(false);
    setCallStatus(null);
    setCallInfo(null);
    setIsMuted(false);
    setConnectionError(null);
    setIsReconnecting(false);

    if (!user?.id) return undefined;

    const initializeSip = async () => {
      try {
        const bootstrap = await sipApi.getSipBootstrap();
        const sipConfig = {
          ...bootstrap,
          displayName:
            `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
            user?.email ||
            "NexusDialer Agent",
        };

        if (!active) return;
        const success = sipService.initializeSIP(
          sipConfig,
          ensureAudioElement(),
        );
        if (!active) {
          sipService.disconnect();
          return;
        }

        // The automatic start-up runs on every sign-in and page load, so its
        // outcome is shown by the call dock (connectionError), not a toast.
        // User-initiated reconnects below still toast.
        setIsInitialized(success);
        if (success) {
          setConnectionError(null);
        } else {
          setConnectionError("Failed to initialize SIP connection");
        }
      } catch (error) {
        if (!active) return;
        setConnectionError(
          error.message || "Unexpected error initializing SIP",
        );
      }
    };

    initializeSip();
    return () => {
      active = false;
      sipService.disconnect();
    };
  }, [ensureAudioElement, user?.id]);

  useEffect(() => {
    if (!user?.id) closeNativeMedia();
    return () => closeNativeMedia();
  }, [closeNativeMedia, user?.id]);

  useEffect(() => {
    if (!isInitialized) return undefined;

    const connectionUnsubscribe = sipService.addConnectionListener((status) => {
      setIsConnected(status === "connected");
      if (status === "connected") {
        setConnectionError(null);
        setIsReconnecting(false);
      } else if (status === "disconnected") {
        setIsReconnecting(true);
      }
    });

    const registrationUnsubscribe = sipService.addRegistrationListener(
      (status, cause) => {
        setIsRegistered(status === "registered");
        if (status === "registered") {
          setConnectionError(null);
          setIsReconnecting(false);
        } else if (status === "failed") {
          setConnectionError(cause || "Unknown registration error");
          setIsReconnecting(false);
        }
      },
    );

    const callUnsubscribe = sipService.addCallListener((event, data) => {
      switch (event) {
        case "incoming": {
          const trackedId = sipService.parseNexusCallId(data.callId || data.id);
          setCallStatus("incoming");
          setCallInfo({
            ...(trackedId ? { id: trackedId, callId: trackedId } : {}),
            direction: "incoming",
            from: data.from,
            displayName: data.displayName,
          });
          break;
        }
        case "outgoing":
          setCallStatus("outgoing");
          setCallInfo({ direction: "outgoing", to: data.to });
          break;
        case "progress":
          setCallStatus("progress");
          break;
        case "accepted":
        case "confirmed":
          setCallStatus("agent_connected");
          break;
        case "ended":
          setCallStatus("ended");
          setCallInfo(null);
          setIsMuted(false);
          break;
        case "failed":
          setCallStatus("failed");
          setCallInfo(null);
          setIsMuted(false);
          toast.error(`Call failed: ${data || "Unknown reason"}`);
          break;
        case "muted":
          setIsMuted(data);
          break;
        default:
          break;
      }
    });

    return () => {
      connectionUnsubscribe();
      registrationUnsubscribe();
      callUnsubscribe();
    };
  }, [isInitialized]);

  const answerCall = useCallback(() => sipService.answerCall(), []);
  const hangupCall = useCallback(() => sipService.hangupCall(), []);

  const toggleMute = useCallback(() => {
    const next = sipService.toggleMute();
    setIsMuted(next);
    return next;
  }, []);

  const sendDTMF = useCallback((tones) => sipService.sendDTMF(tones), []);

  const reconnect = useCallback(async () => {
    if (!user) return false;
    setIsReconnecting(true);

    try {
      sipService.disconnect();
      const bootstrap = await sipApi.getSipBootstrap();
      const success = sipService.initializeSIP(
        {
          ...bootstrap,
          displayName:
            `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
            user?.email ||
            "NexusDialer Agent",
        },
        ensureAudioElement(),
      );

      if (!success) throw new Error("Failed to initialize SIP connection");
      setIsInitialized(true);
      setConnectionError(null);
      toast.success("Reconnected to phone system");
      return true;
    } catch (error) {
      setConnectionError(error.message || "Failed to reconnect");
      toast.error(`Failed to reconnect: ${error.message || "Unknown error"}`);
      return false;
    } finally {
      setIsReconnecting(false);
    }
  }, [ensureAudioElement, user]);

  const value = {
    isInitialized,
    isConnected,
    isRegistered,
    callStatus,
    callInfo,
    isMuted,
    connectionError,
    isReconnecting,
    reconnect,
    answerCall,
    hangupCall,
    toggleMute,
    sendDTMF,
    isInCall: sipService.isInCall,
    setAudioElement,
    nativeCallInfo,
    nativeMedia,
    nativeMediaStatus: nativeMedia.status,
    nativeMediaError: nativeMedia.error,
    isNativeSignalingReady: Boolean(socket && isSocketConnected),
    prepareNativeCall,
    connectNativeAudio,
    hangupNativeCall,
    nativeControl,
    startNativeRecording: () => nativeRecording("startRecording"),
    stopNativeRecording: () => nativeRecording("stopRecording"),
    refreshNativeRecording: () => nativeRecording("recordingStatus"),
    closeNativeMedia,
    getNativePeerConnection: () =>
      nativeSessionRef.current?.getPeerConnection() || null,
  };

  return <SipContext.Provider value={value}>{children}</SipContext.Provider>;
};

export const useSip = () => {
  const context = useContext(SipContext);
  if (!context) {
    throw new Error("useSip must be used within a SipProvider");
  }
  return context;
};

export default SipContext;
